import { valueAt } from "../common/valueAt";
import type { AlgorithmOperation, OperationType } from "@algoquest/schemas";

/**
 * Insertion Sort — framework-independent Algorithm Engine (Master Spec
 * §8, §13, §24, §27). Values are visible from the start (§24), same as
 * Bubble Sort and Selection Sort. Gameplay identity per §27 is "sorted
 * region + shift + insert": the sorted region grows one element per
 * round from the left; each round picks up the first unsorted element
 * as the `key`, then the player walks backward through the sorted
 * region comparing the key against each element in turn, deciding at
 * each one whether that element must "shift" right to make room or
 * whether the key has found its resting place and should be "insert"ed
 * — "the current value is inserted into the correct position by
 * performing algorithmic operations" (§27), never silently relocated.
 *
 * Two-phase interaction per comparison step, mirroring Bubble Sort's and
 * Selection Sort's shape: inspect() reveals the current compareIndex-vs-
 * key pair as a COMPARE event; decide() commits to "shift" or "insert",
 * validated against the engine's own (never-exposed) comparison result —
 * a wrong commitment increments `mistakes` and leaves state unchanged,
 * forcing a retry. Reaching the start of the array (no more sorted-region
 * element to compare against) auto-concludes the round with an INSERT —
 * there is nothing left to decide, exactly like Bubble Sort's automatic
 * PASS_COMPLETE and Selection Sort's automatic PLACE needing no separate
 * player action.
 *
 * Implementation note on the underlying array writes: this engine uses
 * the standard in-place shift technique (copy the compared element one
 * slot right, remembering the key's value separately in `keyValue` until
 * it is finally written back by INSERT). This means `values[keyIndex]`
 * can transiently equal `values[compareIndex]` for one step after a
 * shift and before the next write — a real, correct artifact of how
 * insertion sort works, not a bug — so the presentation layer highlights
 * `keyIndex` distinctly (as the box currently "holding" the key) rather
 * than trying to hide it.
 */
export interface InsertionSortDomainState {
  values: number[];
  sortDirection: "ascending" | "descending";
  /**
   * Size of the sorted region *before this round started*. During a
   * round this stays fixed at that value (the round's key index start);
   * it only advances by 1 once the round's key has been inserted. A box
   * at index `i <= sortedBoundary` is visually part of the finalized
   * sorted region UNLESS `i === keyIndex` (the key's current resting
   * slot, still being carried).
   */
  sortedBoundary: number;
  keyIndex: number;
  keyValue: number;
  compareIndex: number;
  awaitingDecision: boolean;
  comparisons: number;
  /** Named `shifts`, not `swaps` — Master Spec §40 tracks them as distinct metrics. */
  shifts: number;
  passesCompleted: number;
  mistakes: number;
  isComplete: boolean;
}

export interface InsertionSortStepResult {
  operations: AlgorithmOperation[];
  state: InsertionSortDomainState;
}

export interface InsertionSortDecisionResult {
  wasCorrect: boolean;
  result: InsertionSortStepResult | null;
}

export class InsertionSortEngine {
  private readonly values: number[];
  private readonly sortDirection: "ascending" | "descending";
  private sortedBoundary = 1;
  private keyIndex: number;
  private keyValue: number;
  private compareIndex: number;
  private awaitingDecision = false;
  private comparisons = 0;
  private shifts = 0;
  private passesCompleted = 0;
  private mistakes = 0;
  private isComplete = false;
  private sequenceCounter = 0;
  private readonly trace: AlgorithmOperation[] = [];

  constructor(values: number[], sortDirection: "ascending" | "descending") {
    if (values.length < 2) {
      throw new Error("InsertionSortEngine requires at least 2 elements");
    }
    this.values = [...values];
    this.sortDirection = sortDirection;
    this.keyIndex = this.sortedBoundary;
    this.keyValue = valueAt(this.values, this.sortedBoundary);
    this.compareIndex = this.sortedBoundary - 1;
  }

  getState(): InsertionSortDomainState {
    return {
      values: [...this.values],
      sortDirection: this.sortDirection,
      sortedBoundary: this.sortedBoundary,
      keyIndex: this.keyIndex,
      keyValue: this.keyValue,
      compareIndex: this.compareIndex,
      awaitingDecision: this.awaitingDecision,
      comparisons: this.comparisons,
      shifts: this.shifts,
      passesCompleted: this.passesCompleted,
      mistakes: this.mistakes,
      isComplete: this.isComplete,
    };
  }

  getTrace(): AlgorithmOperation[] {
    return [...this.trace];
  }

  /** Reveals the current sorted-region-element-vs-key pair as a COMPARE event. */
  inspect(nowMs: number): InsertionSortStepResult | null {
    if (this.isComplete || this.awaitingDecision) {
      return null;
    }
    const compareIndex = this.compareIndex;
    const keyIndex = this.keyIndex;
    this.comparisons += 1;
    const operations: AlgorithmOperation[] = [
      this.emit("COMPARE", nowMs, {
        indices: [compareIndex, keyIndex],
        values: [valueAt(this.values, compareIndex), this.keyValue],
      }),
    ];
    this.awaitingDecision = true;
    return { operations, state: this.getState() };
  }

  /** The player's "shift" (make room) or "insert" (key rests here) commitment. */
  decide(choice: "shift" | "insert", nowMs: number): InsertionSortDecisionResult {
    if (this.isComplete || !this.awaitingDecision) {
      return { wasCorrect: false, result: null };
    }
    const needsShift = this.needsShift();
    const correctChoice: "shift" | "insert" = needsShift ? "shift" : "insert";
    if (choice !== correctChoice) {
      this.mistakes += 1;
      return { wasCorrect: false, result: null };
    }

    const operations: AlgorithmOperation[] = [];
    this.awaitingDecision = false;

    if (choice === "shift") {
      const compareIndex = this.compareIndex;
      const keyIndex = this.keyIndex;
      const movedValue = valueAt(this.values, compareIndex);
      this.values[keyIndex] = movedValue;
      this.shifts += 1;
      operations.push(this.emit("SHIFT", nowMs, { indices: [compareIndex, keyIndex], values: [movedValue] }));

      this.keyIndex = compareIndex;
      this.compareIndex = compareIndex - 1;

      if (this.compareIndex < 0) {
        // No more sorted-region element to compare against — the key
        // must rest here. Nothing left to decide, so this concludes
        // automatically in the same step, exactly like Bubble Sort's
        // automatic PASS_COMPLETE and Selection Sort's automatic PLACE.
        this.values[this.keyIndex] = this.keyValue;
        operations.push(this.emit("INSERT", nowMs, { indices: [this.keyIndex], values: [this.keyValue] }));
        this.concludeRound();
      }
    } else {
      this.values[this.keyIndex] = this.keyValue;
      operations.push(this.emit("INSERT", nowMs, { indices: [this.keyIndex], values: [this.keyValue] }));
      this.concludeRound();
    }

    return { wasCorrect: true, result: { operations, state: this.getState() } };
  }

  private concludeRound(): void {
    this.passesCompleted += 1;
    this.sortedBoundary += 1;
    if (this.sortedBoundary >= this.values.length) {
      this.isComplete = true;
      return;
    }
    this.keyIndex = this.sortedBoundary;
    this.keyValue = valueAt(this.values, this.sortedBoundary);
    this.compareIndex = this.sortedBoundary - 1;
  }

  private needsShift(): boolean {
    const compareValue = valueAt(this.values, this.compareIndex);
    return this.sortDirection === "ascending" ? compareValue > this.keyValue : compareValue < this.keyValue;
  }

  private emit(
    type: OperationType,
    timestampMs: number,
    partial: Partial<Omit<AlgorithmOperation, "sequence" | "type" | "timestampMs">>,
  ): AlgorithmOperation {
    const operation: AlgorithmOperation = {
      sequence: this.sequenceCounter,
      type,
      timestampMs,
      ...partial,
    };
    this.sequenceCounter += 1;
    this.trace.push(operation);
    return operation;
  }
}
